/**
 * Merge the adapter's parse of a page over our generic parse of the same raw
 * HTML (`parseDetailFromHtml` in @repo/scrape-providers: JSON-LD plus regex
 * heuristics). Providers only fetch; neither input comes from a provider.
 *
 * The adapter parser is authoritative: every field it populates replaces the
 * generic value, and the generic parse only fills gaps. An empty parser value
 * never replaces anything, except an explicit null for a field listed here,
 * which clears the generic guess:
 * - Domain: its __NEXT_DATA__ listing record (listingByIdV2) is the site's own
 *   data. A null salePrice/price means Domain holds no sold amount (price
 *   withheld), so a guide price must not become the sale price.
 * - REA: salePrice=null alongside a priceDisplay means the listing's own price
 *   is withheld ("Contact agent"), a range, or an implausible figure, which
 *   disproves a single guessed amount (e.g. a rent taken from the description).
 *   Its features (bedrooms through landSize) come from the listing record or the
 *   listing header, so a null there means REA states none, and the generic
 *   guess must go: JSON-LD "Residence" as the type, floorSize as the land size.
 *   A null saleDate means the listing record is there and holds no sold date, so a
 *   date the generic parse took from another listing's card must go too.
 * - The agency parsers below read the site's own listing block and return {}
 *   for any page they do not recognise, so a null they return means the page
 *   shows no such value. Without the clear, the generic parse's guesses on
 *   these pages survive: the corporate office's JSON-LD address and "WebSite"
 *   type on Jellis Craig, a price-guide bound or a stray "$1,191" as the
 *   price display (which normalizeExtractedData then turns into a sale price).
 */
const LISTING_RECORD_FIELDS: ReadonlySet<string> = new Set([
  "saleDate", "salePrice", "price", "priceDisplay", "bedrooms", "bathrooms", "parking",
  "propertyType", "landSize", "landSizeUnit",
]);

// The generic parse calls any page that mentions "sold" a sold listing; an
// agency parser's null listingType (no sold badge, or allhomes' never-sold
// property) overrides that guess.
const AGENCY_RECORD_FIELDS: ReadonlySet<string> = new Set([...LISTING_RECORD_FIELDS, "listingType"]);

const REA_LISTING_RECORD_FIELDS: ReadonlySet<string> = new Set([
  "saleDate", "bedrooms", "bathrooms", "parking", "propertyType", "landSize", "landSizeUnit",
]);

const PARSER_NULL_CLEARS: Readonly<Record<string, ReadonlySet<string>>> = {
  "realestate-com-au": REA_LISTING_RECORD_FIELDS,
  "domain-com-au": LISTING_RECORD_FIELDS,
  "jellis-craig": AGENCY_RECORD_FIELDS,
  "belle-property": AGENCY_RECORD_FIELDS,
  "barry-plant": AGENCY_RECORD_FIELDS,
  "toop-and-toop": AGENCY_RECORD_FIELDS,
  realmark: AGENCY_RECORD_FIELDS,
  "allhomes-com-au": AGENCY_RECORD_FIELDS,
  buxton: AGENCY_RECORD_FIELDS,
};

function hasValue(value: unknown): boolean {
  return value !== null && value !== undefined && (typeof value !== "string" || value.trim() !== "");
}

function parserWins(field: string, value: unknown, parser: Record<string, unknown>, adapterSlug?: string): boolean {
  if (hasValue(value)) return true;
  if (value !== null) return false;
  if (adapterSlug === "realestate-com-au" && field === "salePrice") return hasValue(parser.priceDisplay);
  return PARSER_NULL_CLEARS[adapterSlug ?? ""]?.has(field) ?? false;
}

export function mergeAdapterParserData(
  generic: Record<string, unknown>,
  parser: Record<string, unknown>,
  adapterSlug?: string,
): Record<string, unknown> {
  return mergeAdapterParserDataWithSources(generic, parser, adapterSlug).data;
}

export type ParserFieldSource = "generic-parse" | "adapter-parser";

/** The production merge plus, per field, which parse supplied the value. */
export function mergeAdapterParserDataWithSources(
  generic: Record<string, unknown>,
  parser: Record<string, unknown>,
  adapterSlug?: string,
): { data: Record<string, unknown>; sources: Record<string, ParserFieldSource> } {
  const data = { ...generic };
  const sources: Record<string, ParserFieldSource> = {};
  for (const field of Object.keys(generic)) sources[field] = "generic-parse";
  for (const [field, value] of Object.entries(parser)) {
    if (!parserWins(field, value, parser, adapterSlug)) continue;
    data[field] = value;
    sources[field] = "adapter-parser";
  }
  return { data, sources };
}
